import { useEffect, useMemo, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  CalendarClock,
  Loader2,
  XCircle,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import api from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, fmtDateTime } from "./_shared";

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

// Static recharts styling — hoisted so each render passes the same object
// reference (otherwise recharts treats every render as a config change and
// retriggers internal memoisation).
const CHART_MARGIN = { top: 8, right: 12, left: 0, bottom: 8 };
const AXIS_LINE = { stroke: "#e2e8f0" };
const TOOLTIP_STYLE = {
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  fontSize: 12,
};
const LEGEND_STYLE = { fontSize: 12, paddingTop: 4 };
const BAR_RADIUS_TOP = [6, 6, 0, 0];
const BAR_RADIUS_FLAT = [0, 0, 0, 0];

function fmtMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return AUD.format(Number(n));
}

function pctDelta(current, prev) {
  if (prev == null || prev === 0) {
    return current > 0 ? { value: null, label: "new period" } : null;
  }
  const delta = ((current - prev) / prev) * 100;
  return {
    value: delta,
    label: `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`,
    positive: delta >= 0,
  };
}

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/reports/overview");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled)
          setErr(e?.response?.data?.detail || e.message || "Failed to load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const revenueDelta = useMemo(() => {
    if (!data) return null;
    return pctDelta(
      data.current_month.revenue_aud,
      data.prev_year_same_month.revenue_aud,
    );
  }, [data]);

  // Memoised aggregates — keeps the JSX render path off the hot loop for
  // these reduces / chart shape transforms.
  const totals = useMemo(() => {
    if (!data) return null;
    const revenueRows = data.revenue_12m || [];
    const cancelRows = data.cancellations_12m || [];
    return {
      revenueMembership: revenueRows.reduce(
        (s, r) => s + (r.membership || 0),
        0,
      ),
      revenueArchive: revenueRows.reduce((s, r) => s + (r.archive || 0), 0),
      cancellations: cancelRows.reduce((s, r) => s + (r.count || 0), 0),
    };
  }, [data]);

  const revenueChartData = useMemo(() => {
    if (!data) return [];
    return (data.revenue_12m || []).map((r) => ({
      month: r.label.slice(0, 3) + " " + r.label.slice(-2),
      Membership: r.membership || 0,
      Archive: r.archive || 0,
    }));
  }, [data]);

  const cancellationsChartData = useMemo(() => {
    if (!data) return [];
    return (data.cancellations_12m || []).map((r) => ({
      month: r.label.slice(0, 3) + " " + r.label.slice(-2),
      count: r.count,
    }));
  }, [data]);

  return (
    <AdminLayout
      title="Reporting"
      subtitle={
        data
          ? `Snapshot as of ${fmtDateTime(data.as_of)}. All figures AUD.`
          : "Loading platform KPIs and 12-month trends…"
      }
    >
      {loading ? (
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-reports-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div
          className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
          data-testid="admin-reports-error"
        >
          {String(err)}
        </div>
      ) : (
        <div className="space-y-8" data-testid="admin-reports-content">
          <section
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="admin-reports-kpis"
          >
            <Kpi
              icon={Users}
              label={`Sign-ups · ${data.current_month.label}`}
              primary={data.current_month.signups.toLocaleString()}
              secondary={`Total users: ${data.totals.users.toLocaleString()}`}
              testid="kpi-signups"
            />
            <Kpi
              icon={DollarSign}
              label={`Revenue · ${data.current_month.label}`}
              primary={fmtMoney(data.current_month.revenue_aud)}
              secondary="paid this month"
              testid="kpi-revenue-month"
            />
            <Kpi
              icon={CalendarClock}
              label={`Revenue · ${data.prev_year_same_month.label}`}
              primary={fmtMoney(data.prev_year_same_month.revenue_aud)}
              secondary="same month last year"
              testid="kpi-revenue-prev-year"
            />
            <Kpi
              icon={revenueDelta?.positive ? TrendingUp : TrendingDown}
              label="YoY change"
              primary={revenueDelta?.label || "—"}
              secondary={`vs ${data.prev_year_same_month.label}`}
              tone={
                revenueDelta == null
                  ? "slate"
                  : revenueDelta.positive
                    ? "emerald"
                    : "rose"
              }
              testid="kpi-yoy"
            />
          </section>

          <Card className="p-5" testid="admin-reports-revenue-chart-card">
            <header className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h2 className="text-sm font-medium text-slate-900">
                  Monthly revenue · last 12 months
                </h2>
                <p className="text-xs text-slate-500">
                  Membership and Archive plan revenue, stacked. Each bar shows
                  the total paid into Stripe that month (AUD inc. GST).
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center justify-end gap-3 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
                    Membership
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-500" />
                    Archive
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Membership{" "}
                  <span className="font-medium text-emerald-700">
                    {fmtMoney(totals?.revenueMembership || 0)}
                  </span>{" "}
                  · Archive{" "}
                  <span className="font-medium text-amber-700">
                    {fmtMoney(totals?.revenueArchive || 0)}
                  </span>
                </div>
              </div>
            </header>
            <div
              className="h-72 w-full"
              data-testid="admin-reports-revenue-chart"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={revenueChartData}
                  margin={CHART_MARGIN}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={AXIS_LINE}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickFormatter={(v) => AUD.format(v).replace("A$", "$")}
                    tickLine={false}
                    axisLine={AXIS_LINE}
                    width={64}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v, name) => [fmtMoney(v), name]}
                  />
                  <Legend
                    wrapperStyle={LEGEND_STYLE}
                    iconType="square"
                  />
                  <Bar
                    dataKey="Membership"
                    stackId="rev"
                    fill="#10b981"
                    radius={BAR_RADIUS_FLAT}
                  />
                  <Bar
                    dataKey="Archive"
                    stackId="rev"
                    fill="#f59e0b"
                    radius={BAR_RADIUS_TOP}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-5" testid="admin-reports-cancellations-chart-card">
            <header className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-medium text-slate-900 inline-flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-rose-500" />
                  Subscription cancellations · last 12 months
                </h2>
                <p className="text-xs text-slate-500">
                  Counts subscriptions flagged to cancel (or set inactive) per month.
                </p>
              </div>
              <span className="text-xs text-slate-500">
                Total:{" "}
                <span className="font-medium text-slate-700">
                  {(totals?.cancellations || 0).toLocaleString()}
                </span>
              </span>
            </header>
            <div
              className="h-72 w-full"
              data-testid="admin-reports-cancellations-chart"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={cancellationsChartData}
                  margin={CHART_MARGIN}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="month"
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={AXIS_LINE}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={AXIS_LINE}
                    allowDecimals={false}
                    width={32}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v) => [v, "Cancellations"]}
                  />
                  <Bar dataKey="count" fill="#f43f5e" radius={BAR_RADIUS_TOP} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}

function Kpi({ icon: Icon, label, primary, secondary, tone = "slate", testid }) {
  const toneCls = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
  }[tone];
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.12em] text-slate-500">
          {label}
        </div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div
        className={`mt-3 text-3xl font-medium tracking-tight ${toneCls}`}
      >
        {primary}
      </div>
      <div className="mt-1 text-xs text-slate-500">{secondary}</div>
    </div>
  );
}
